import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import "./AssetInventoryList.css";

import {
  FaSearch,
  FaPlus,
  FaEye,
  FaEdit,
  FaLaptop,
  FaFilter,
} from "react-icons/fa";

const AssetInventoryList = () => {
  const navigate = useNavigate();

  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    asset_status: "",
    brand: "",
    location: "",
  });

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const [editAsset, setEditAsset] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchAssets();
  }, []);

  /* ============================
          Load Assets
  ============================ */

  const fetchAssets = async () => {
    try {
      setLoading(true);

      const data = await apiClient("/assetinventory/inventory/list/");

      const list = Array.isArray(data)
        ? data
        : data.results || [];

      setAssets(list);
      setFilteredAssets(list);

      setLoading(false);

    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ============================
         Search + Filters
  ============================ */

  useEffect(() => {

    let data = [...assets];

    if (searchTerm) {

      data = data.filter(
        (item) =>
          item.asset_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||

          item.asset_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||

          item.serial_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||

          item.brand
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||

          item.model
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    if (filters.asset_status) {
      data = data.filter(
        (item) =>
          item.asset_status === filters.asset_status
      );
    }

    if (filters.brand) {
      data = data.filter(
        (item) =>
          item.brand === filters.brand
      );
    }

    if (filters.location) {
      data = data.filter(
        (item) =>
          item.location === filters.location
      );
    }

    setFilteredAssets(data);

  }, [assets, searchTerm, filters]);

  /* ============================
         Dropdown Data
  ============================ */

  const uniqueBrands = [
    ...new Set(
      assets
        .map((item) => item.brand)
        .filter(Boolean)
    ),
  ];

  const uniqueLocations = [
    ...new Set(
      assets
        .map((item) => item.location)
        .filter(Boolean)
    ),
  ];

  /* ============================
         View Asset
  ============================ */

  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
  };

  /* ============================
         Edit Asset
  ============================ */

  const handleEdit = (asset) => {
    setEditAsset({ ...asset });
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {

    const { name, value } = e.target;

    setEditAsset((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ============================
          Save Asset
  ============================ */

  const handleUpdate = async () => {

    try {

      await apiClient(`/assetinventory/inventory/update/${editAsset.id}/`, 

        {
          method: "PUT",

          // headers: {
          //   "Content-Type": "application/json",
          // },

          body: JSON.stringify(editAsset),
        }

      );

      alert("Asset Updated Successfully");

      setShowEditModal(false);

      fetchAssets();

    } catch (err) {
      console.error(err);
      alert("Unable to update asset.");
    }

  };

  /* ============================
        Status Badge
  ============================ */

  const getStatusClass = (status) => {

    switch (status) {

      case "Available":
        return "inventory-status available";

      case "Assigned":
        return "inventory-status assigned";

      case "Repair":
        return "inventory-status repair";

      case "Damaged":
        return "inventory-status damaged";

      case "Scrapped":
        return "inventory-status scrapped";

      default:
        return "inventory-status";
    }

  };

  if (loading) {
    return (
      <div className="inventory-loading">
        Loading Assets...
      </div>
    );
  }
    return (
    <div className="inventory-page">

      {/* Header */}
      <div className="inventory-header">

        <div>
          <h2>Asset Inventory</h2>
          <p>Manage company hardware inventory</p>
        </div>

        <button
          className="inventory-add-btn"
          onClick={() =>
            navigate("/dashboard/ManagerAcess/newasset")
          }
        >
          <FaPlus />
          Add Asset
        </button>

      </div>

      {/* Toolbar */}

      <div className="inventory-toolbar">

        <div className="inventory-search">

          <FaSearch />

          <input
            type="text"
            placeholder="Search Asset Number, Brand, Model..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
          />

        </div>

        <button
          className="inventory-filter-btn"
          onClick={() =>
            setShowFilters(!showFilters)
          }
        >
          <FaFilter />
          Filters
        </button>

      </div>

      {/* Filter */}

      {showFilters && (

        <div className="inventory-filter-panel">

          <select
            value={filters.asset_status}
            onChange={(e) =>
              setFilters({
                ...filters,
                asset_status: e.target.value,
              })
            }
          >
            <option value="">All Status</option>
            <option>Available</option>
            <option>Assigned</option>
            <option>Repair</option>
            <option>Damaged</option>
            <option>Scrapped</option>
          </select>

          <select
            value={filters.brand}
            onChange={(e) =>
              setFilters({
                ...filters,
                brand: e.target.value,
              })
            }
          >

            <option value="">All Brands</option>

            {uniqueBrands.map((b) => (
              <option key={b}>{b}</option>
            ))}

          </select>

          <select
            value={filters.location}
            onChange={(e) =>
              setFilters({
                ...filters,
                location: e.target.value,
              })
            }
          >

            <option value="">All Locations</option>

            {uniqueLocations.map((l) => (
              <option key={l}>{l}</option>
            ))}

          </select>

        </div>

      )}

      {/* Table */}

      <div className="inventory-table-card">

        <table className="inventory-table">

          <thead>

            <tr>

              <th>Asset Name</th>

              <th>Asset No</th>
                
              <th>Brand</th>

              <th>Model</th>

              <th>Serial No</th>

              <th>Processor</th>

              <th>RAM</th>

              <th>Storage</th>

              <th>Location</th>

              <th>Status</th>

              <th>Actions</th>

            </tr>

          </thead>

          <tbody>

            {filteredAssets.length > 0 ? (

              filteredAssets.map((item) => (

                <tr key={item.id}>

                    <td>
                        <span className="asset-type">
                        {item.asset_name}
                        </span>
                    </td>

                    <td>{item.asset_number}</td>

                    <td>{item.brand}</td>

                  <td>{item.model}</td>

                  <td>{item.serial_number}</td>

                  <td>{item.processor}</td>

                  <td>{item.ram}</td>

                  <td>{item.storage}</td>

                  <td>{item.location}</td>

                  <td>

                    <span
                      className={getStatusClass(
                        item.asset_status
                      )}
                    >
                      {item.asset_status}
                    </span>

                  </td>

                  <td>

                    <div className="inventory-actions">

                      <FaEye
                        className="inventory-view"
                        title="View"
                        onClick={() =>
                          handleView(item)
                        }
                      />

                      <FaEdit
                        className="inventory-edit"
                        title="Edit"
                        onClick={() =>
                          handleEdit(item)
                        }
                      />

                    </div>

                  </td>

                </tr>

              ))

            ) : (

              <tr>

                <td
                  colSpan="11"
                  style={{
                    textAlign: "center",
                    padding: "30px",
                  }}
                >
                  No Assets Found
                </td>

              </tr>

            )}

          </tbody>

        </table>

      </div>

      {/* View Modal */}

      {showViewModal && selectedAsset && (

        <div className="inventory-modal-overlay">

          <div className="inventory-modal">

            <div className="inventory-modal-header">

              <h3>

                <FaLaptop /> Asset Details

              </h3>

              <button
                onClick={() =>
                  setShowViewModal(false)
                }
              >
                ✕

              </button>

            </div>

            <div className="inventory-details-grid">

              <div><strong>Asset No:</strong> {selectedAsset.asset_number}</div>

              <div><strong>Asset Name:</strong>{selectedAsset.asset_name}</div>

              <div><strong>Brand:</strong> {selectedAsset.brand}</div>

              <div><strong>Model:</strong> {selectedAsset.model}</div>

              <div><strong>Serial:</strong> {selectedAsset.serial_number}</div>

              <div><strong>Processor:</strong> {selectedAsset.processor}</div>

              <div><strong>RAM:</strong> {selectedAsset.ram}</div>

              <div><strong>Storage:</strong> {selectedAsset.storage}</div>

              <div><strong>Purchase Date:</strong> {selectedAsset.purchase_date}</div>

              <div><strong>Warranty:</strong> {selectedAsset.warranty_expiry}</div>

              <div><strong>Vendor:</strong> {selectedAsset.vendor}</div>

              <div><strong>Cost:</strong> ₹{selectedAsset.purchase_cost}</div>

              <div><strong>Location:</strong> {selectedAsset.location}</div>

              <div><strong>Status:</strong> {selectedAsset.asset_status}</div>

              <div className="inventory-full-width">

                <strong>Remarks</strong>

                <p>{selectedAsset.remarks || "-"}</p>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* Edit Modal */}

      {showEditModal && editAsset && (

        <div className="inventory-modal-overlay">

          <div className="inventory-modal">

            <div className="inventory-modal-header">

              <h3>Edit Asset</h3>

              <button
                onClick={() =>
                  setShowEditModal(false)
                }
              >
                ✕

              </button>

            </div>

            {/* <div className="inventory-modal">

  <div className="inventory-modal-header">
    <h3>Edit Asset</h3>

    <button onClick={() => setShowEditModal(false)}>
      ✕
    </button>
  </div> */}

  {/* Scrollable Body */}
  <div className="inventory-modal-body">

    <div className="inventory-edit-grid">

      <div className="form-group">
        <label>Asset Name</label>
        <select
          name="asset_name"
          value={editAsset.asset_name || ""}
          onChange={handleEditChange}
        >
          <option value="Laptop">Laptop</option>
          <option value="Desktop">Desktop</option>
          <option value="Monitor">Monitor</option>
          <option value="Keyboard">Keyboard</option>
          <option value="Mouse">Mouse</option>
          <option value="Dock">Dock</option>
          <option value="Printer">Printer</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="form-group">
        <label>Brand</label>
        <input
          name="brand"
          value={editAsset.brand}
          onChange={handleEditChange}
        />
      </div>

      <div className="form-group">
        <label>Model</label>
        <input
          name="model"
          value={editAsset.model}
          onChange={handleEditChange}
        />
      </div>

      <div className="form-group">
        <label>Processor</label>
        <input
          name="processor"
          value={editAsset.processor}
          onChange={handleEditChange}
        />
      </div>

      <div className="form-group">
        <label>RAM</label>
        <input
          name="ram"
          value={editAsset.ram}
          onChange={handleEditChange}
        />
      </div>

      <div className="form-group">
        <label>Storage</label>
        <input
          name="storage"
          value={editAsset.storage}
          onChange={handleEditChange}
        />
      </div>

      <div className="form-group">
        <label>Location</label>
        <input
          name="location"
          value={editAsset.location}
          onChange={handleEditChange}
        />
      </div>

      <div className="form-group">
        <label>Status</label>
        <select
          name="asset_status"
          value={editAsset.asset_status}
          onChange={handleEditChange}
        >
          <option>Available</option>
          <option>Assigned</option>
          <option>Repair</option>
          <option>Damaged</option>
          <option>Scrapped</option>
        </select>
      </div>

    </div>

  </div>

  {/* Fixed Footer */}
  <div className="inventory-modal-footer">
    <button
      className="inventory-cancel-btn"
      onClick={() => setShowEditModal(false)}
    >
      Cancel
    </button>

    <button
      className="inventory-save-btn"
      onClick={handleUpdate}
    >
      Save Changes
    </button>
  </div>

</div>
 

          </div>


      )}

    </div>
  );
};

export default AssetInventoryList;